import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  MessageSquare,
  Calculator,
  Activity,
  FileText,
  Clock,
  Star,
  Sun,
  Moon,
  LogOut,
  Settings,
  HelpCircle,
  UserCircle,
  BookOpen,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import QuotaLimitModal from '../../components/QuotaLimitModal';
import HealthDataConsent from '../../components/HealthDataConsent';
import consentService from '../../Services/consentService.js';

const QUOTA_USED = 250;
const QUOTA_TOTAL = 500;
const CIRCLE_RADIUS = 20;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;
const STROKE_DASHOFFSET = CIRCLE_CIRCUMFERENCE * (1 - QUOTA_USED / QUOTA_TOTAL);

const QUICK_ACTIONS = [
  {
    label: 'Chat Clinique IA',
    description: "Posez vos questions cliniques à l'IA médicale",
    icon: MessageSquare,
    iconColor: 'text-teal-600 dark:text-teal-400',
    iconBg: 'bg-teal-500/10 dark:bg-teal-500/20',
    iconBgHover: 'group-hover:bg-teal-500/20 dark:group-hover:bg-teal-500/30',
    borderHover: 'hover:border-teal-400 dark:hover:border-teal-500',
    route: '/pro/infirmier/chat',
  },
  {
    label: 'Calculateurs',
    description: 'Doses, débits, PSE en un clic',
    icon: Calculator,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-500/10 dark:bg-blue-500/20',
    iconBgHover: 'group-hover:bg-blue-500/20 dark:group-hover:bg-blue-500/30',
    borderHover: 'hover:border-blue-400 dark:hover:border-blue-500',
    route: '/pro/calculateurs',
  },
  {
    label: 'Scores Cliniques',
    description: 'Glasgow, Braden, EVA...',
    icon: Activity,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-500/10 dark:bg-indigo-500/20',
    iconBgHover: 'group-hover:bg-indigo-500/20 dark:group-hover:bg-indigo-500/30',
    borderHover: 'hover:border-indigo-400 dark:hover:border-indigo-500',
    route: '/pro/scores',
  },
  {
    label: 'Bibliothèque Médicaments',
    description: 'Fiches IDE, posologie, surveillance, chat IA',
    icon: FileText,
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-500/10 dark:bg-purple-500/20',
    iconBgHover: 'group-hover:bg-purple-500/20 dark:group-hover:bg-purple-500/30',
    borderHover: 'hover:border-purple-400 dark:hover:border-purple-500',
    route: '/pro/medicaments',
  },
];

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const diffMins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Hier';
  return `Il y a ${diffDays} jours`;
};

const TYPE_BADGE = {
  calcul: 'bg-teal-100 text-teal-700 dark:bg-teal-500/20 dark:text-teal-400',
  medicament: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
};


const DashboardInfirmier = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [showHealthConsent, setShowHealthConsent] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [pinnedProtocols, setPinnedProtocols] = useState([]);
  const [showAllProtocols, setShowAllProtocols] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'users', user.uid, 'protocols'),
      where('pinned', '==', true),
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPinnedProtocols(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('[pinnedProtocols] onSnapshot error:', error);
    });
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'users', user.uid, 'recentSearches'),
      orderBy('timestamp', 'desc'),
      limit(5)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      console.log('[recentSearches] uid:', user.uid, 'docs:', docs);
      setRecentSearches(docs);
    }, (error) => {
      console.error('[recentSearches] onSnapshot error:', error);
    });
    return () => unsubscribe();
  }, [user?.uid]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Déconnexion réussie');
      navigate('/auth/pro');
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const lastName = user?.last_name || 'Dupont';
  const prefix = user?.profile_type === 'medecin' ? 'Dr.' : 'IDE.';
const displayName = `${prefix} ${lastName}`;
  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    : 'JD';

  useEffect(() => {
    const checkConsent = async () => {
      if (user?.uid) {
        const hasConsent = await consentService.hasHealthDataConsent(user.uid);
        if (!hasConsent) setShowHealthConsent(true);
      }
    };
    checkConsent();
  }, [user]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 transition-colors duration-200">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/RemaH.png"
              alt="Remadia Pro"
              className="w-10 h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-xl font-bold text-[#003D82] dark:text-blue-400">Remadia</span>
            <span className="px-2 py-1 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-full border border-teal-500/20 dark:border-teal-500/30">
              PRO
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Widget quota */}
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
              <div className="relative w-12 h-12">
                <svg className="transform -rotate-90" width="48" height="48">
                  <circle cx="24" cy="24" r={CIRCLE_RADIUS} stroke={isDark ? '#475569' : '#E2E8F0'} strokeWidth="4" fill="none" />
                  <circle
                    cx="24" cy="24" r={CIRCLE_RADIUS}
                    stroke="#14B8A6"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={CIRCLE_CIRCUMFERENCE}
                    strokeDashoffset={STROKE_DASHOFFSET}
                    className="transition-all duration-500"
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[#003D82] dark:text-blue-400">
                  {QUOTA_USED}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Quota mensuel</p>
                <p className="text-sm font-semibold text-[#003D82] dark:text-blue-400">{QUOTA_USED} / {QUOTA_TOTAL}</p>
              </div>
            </div>

            {/* Toggle thème */}
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              aria-label="Changer de thème"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            {/* Profil */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200">
                  <div className="w-8 h-8 rounded-full bg-[#003D82] dark:bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">{displayName}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100">
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/profil')}
                  className="dark:hover:bg-slate-700"
                >
                  <UserCircle className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/compte')}
                  className="dark:hover:bg-slate-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Gestion du compte
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/pro/infirmier/faq')}
                  className="dark:hover:bg-slate-700"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Aide / FAQ
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive dark:text-red-400 dark:hover:bg-slate-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003D82] to-[#0056B3] dark:from-slate-800 dark:to-teal-900 text-white py-12 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl font-bold mb-2">
            Bonjour {displayName} 👋
          </h1>
          <p className="text-blue-100 dark:text-blue-200">
            Prêt à gagner du temps sur vos décisions cliniques ?
          </p>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-6">Actions rapides</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <div
                key={action.label}
                onClick={() => navigate(action.route)}
                className={`group bg-white dark:bg-slate-800 p-6 rounded-2xl border-2 border-slate-200 dark:border-slate-700 ${action.borderHover} hover:shadow-lg dark:hover:shadow-slate-900/50 transition-all duration-200 cursor-pointer`}
              >
                <div className={`w-14 h-14 rounded-xl ${action.iconBg} ${action.iconBgHover} flex items-center justify-center mb-4 transition-colors duration-200`}>
                  <Icon className={`w-7 h-7 ${action.iconColor}`} />
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">{action.label}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">{action.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Widgets */}
      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Recherches récentes */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Recherches récentes</h3>
              <Clock className="w-5 h-5 text-slate-400 dark:text-slate-500" />
            </div>
            <div className="space-y-3">
              {recentSearches.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">Aucune recherche récente</p>
              ) : (
                <>
                  {(showAll ? recentSearches : recentSearches.slice(0, 3)).map((item) => (
                    <div
                      key={item.id}
                      onClick={
                        item.type === 'medicament'
                          ? () => navigate('/pro/medicaments', { state: { searchTerm: item.label } })
                          : item.type === 'calcul'
                          ? () => navigate('/pro/calculateurs', { state: { calculatorName: item.label } })
                          : undefined
                      }
                      className="p-3 bg-slate-50 dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors duration-200 cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 leading-snug">{item.label}</p>
                        {item.type && (
                          <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${TYPE_BADGE[item.type] ?? TYPE_BADGE.calcul}`}>
                            {item.type === 'medicament' ? 'Médicament' : 'Calcul'}
                          </span>
                        )}
                      </div>
                      {item.detail && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{item.detail}</p>
                      )}
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{formatRelativeTime(item.timestamp)}</p>
                    </div>
                  ))}
                  {recentSearches.length > 3 && (
                    <button
                      onClick={() => setShowAll((v) => !v)}
                      className="w-full text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium py-1 transition-colors duration-200"
                    >
                      {showAll ? 'Voir moins' : `Voir plus (${recentSearches.length - 3})`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Protocoles épinglés */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Mes protocoles</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/pro/protocoles')}
                  className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                >
                  Voir tout
                </button>
                <BookOpen className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
            </div>
            <div className="space-y-3">
              {pinnedProtocols.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-4">
                  Aucun protocole épinglé — épinglez vos protocoles favoris
                </p>
              ) : (
                <>
                  {(showAllProtocols ? pinnedProtocols : pinnedProtocols.slice(0, 3)).map((protocol) => (
                    <div
                      key={protocol.id}
                      onClick={() => navigate('/pro/protocoles')}
                      className="flex items-start gap-3 p-3 bg-slate-50 hover:bg-teal-50 dark:bg-slate-700 dark:hover:bg-teal-500/10 rounded-lg transition-colors duration-200 cursor-pointer"
                    >
                      <FileText className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 leading-snug truncate">
                          {protocol.titre}
                        </p>
                        {protocol.categorie && (
                          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                            {protocol.categorie}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {pinnedProtocols.length > 3 && (
                    <button
                      onClick={() => setShowAllProtocols((v) => !v)}
                      className="w-full text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium py-1 transition-colors duration-200"
                    >
                      {showAllProtocols ? 'Voir moins' : `Voir plus (${pinnedProtocols.length - 3})`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Statistiques */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Ce mois-ci</h3>
              <Activity className="w-5 h-5 text-slate-400 dark:text-slate-500" />
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Requêtes IA</span>
                  <span className="text-lg font-bold text-[#003D82] dark:text-blue-400">{QUOTA_USED}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-teal-500 rounded-full transition-all duration-500"
                    style={{ width: `${(QUOTA_USED / QUOTA_TOTAL) * 100}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-300">Temps économisé</span>
                <span className="text-lg font-bold text-teal-600 dark:text-teal-400">~8h</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-300">Protocoles consultés</span>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">12</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {showHealthConsent && (
        <HealthDataConsent userId={user.uid} onClose={() => setShowHealthConsent(false)} />
      )}
      {showQuotaModal && (
        <QuotaLimitModal
          isOpen={showQuotaModal}
          onClose={() => setShowQuotaModal(false)}
          onUpgrade={() => navigate('/pricing')}
        />
      )}
    </div>
  );
};

export default DashboardInfirmier;
